/**
 * v4.7.0 — Demo Mode context + hook.
 *
 * Single source of truth for the calling user's Master Demo Mode
 * state. Loaded once on auth, refreshed when the toggle endpoint is
 * called. Exposes:
 *
 *   active     — bool, whether Demo Mode is on for THIS user session
 *   canAccess  — bool, whether the user is allowed to see the toggle
 *   loaded     — bool, has the initial GET resolved
 *   toggle()   — flip the state on the server + locally
 *   set(bool)  — explicit set
 *   refresh()  — re-fetch from server (rare)
 *
 * Mounted in App.js below DemoProvider (which handles the *guided
 * narrative* — distinct from this *environment lens*).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

const DemoModeCtx = createContext({
    active: false,
    canAccess: false,
    loaded: false,
    toggle: async () => {},
    set: async () => {},
    refresh: async () => {},
});

export const DemoModeProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [active, setActive] = useState(false);
    const [canAccess, setCanAccess] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const refresh = useCallback(async () => {
        if (!isAuthenticated) {
            setActive(false); setCanAccess(false); setLoaded(true);
            return;
        }
        try {
            const res = await api.get("/operational-base/demo-mode");
            setActive(!!res.data.active);
            setCanAccess(!!res.data.can_access);
        } catch {
            setActive(false);
            setCanAccess(false);
        } finally {
            setLoaded(true);
        }
    }, [isAuthenticated]);

    useEffect(() => { refresh(); }, [refresh, user?.id]);

    const toggle = useCallback(async () => {
        try {
            const res = await api.post("/operational-base/demo-mode/toggle");
            setActive(!!res.data.active);
            return !!res.data.active;
        } catch (e) {
            return active;
        }
    }, [active]);

    const set = useCallback(async (next) => {
        try {
            const res = await api.post("/operational-base/demo-mode", { active: !!next });
            setActive(!!res.data.active);
        } catch { /* noop */ }
    }, []);

    return (
        <DemoModeCtx.Provider value={{ active, canAccess, loaded, toggle, set, refresh }}>
            {children}
        </DemoModeCtx.Provider>
    );
};

export const useDemoMode = () => useContext(DemoModeCtx);
